const CHART_COLORS = [
  '#3b82f6',
  '#ef4444',
  '#10b981',
  '#f59e0b',
  '#8b5cf6',
  '#ec4899',
  '#06b6d4',
  '#84cc16',
  '#f97316',
  '#14b8a6',
];

const SECTION_LABELS = {
  overview: 'Overview',
  competitors: 'Competitors',
  trends: 'Market Trends',
  'trend-analysis': 'Trend Analyses',
  'market-share': 'Market Share',
  seo: 'SEO Landscape',
  events: 'Events and Headlines',
  'deep-dives': 'Deep Dives',
  sources: 'Sources and Further Reading',
  'selected-comparison': 'Selected Comparison',
};

export function generateTabHtml(tab, options = {}) {
  const report = renderTabReport(tab, {
    prefix: options.prefix || 'tab-1',
    sectionId: options.sectionId || 'tab-report',
    includeTitle: false,
  });

  return renderDocument({
    title: `Market Research: ${safeText(tab?.product || tab?.title || 'Research Report')}`,
    headerHtml: renderReportHeader({
      title: `Market Research: ${safeText(tab?.product || tab?.title || 'Research Report')}`,
      subtitle: metaLine([
        tab?.location ? `Location: ${safeText(tab.location)}` : '',
        tab?.yearsBack ? `Research window: ${safeText(tab.yearsBack)} years` : '',
        `Generated: ${formatDateTime(new Date())}`,
      ]),
    }),
    tocHtml: renderToc(report.tocItems),
    bodyHtml: report.html,
    charts: report.charts,
  });
}

export function generateProjectHtml({ projectName, tabs = [], activeTabId } = {}) {
  const safeProjectName = safeText(projectName || 'Active Project');
  const reports = tabs.map((tab, index) => renderTabReport(tab, {
    prefix: `project-tab-${index + 1}`,
    sectionId: `project-tab-${index + 1}`,
    includeTitle: true,
    tabIndex: index,
    isActive: tab?.id === activeTabId,
  }));

  const researchedCount = tabs.filter((tab) => !!tab?.data).length;
  const projectToc = tabs.map((tab, index) => ({
    id: `project-tab-${index + 1}`,
    label: `${safeText(tab?.title || tab?.product || `Tab ${index + 1}`)}${tab?.id === activeTabId ? ' (active)' : ''}`,
  }));
  const sectionToc = reports.flatMap((report, index) =>
    report.tocItems.map((item) => ({
      id: item.id,
      label: `${safeText(tabs[index]?.title || tabs[index]?.product || `Tab ${index + 1}`)} - ${item.label}`,
    }))
  );

  return renderDocument({
    title: `Market Research Project: ${safeProjectName}`,
    headerHtml: renderReportHeader({
      title: `Market Research Project: ${safeProjectName}`,
      subtitle: metaLine([
        `${tabs.length} tabs`,
        `${researchedCount} with research results`,
        `Generated: ${formatDateTime(new Date())}`,
      ]),
    }),
    tocHtml: renderToc([...projectToc, ...sectionToc]),
    bodyHtml: `
      <section class="card report-cover" id="project-overview">
        <h2>Project Overview</h2>
        <div class="metric-grid">
          ${renderMetric('Tabs', tabs.length)}
          ${renderMetric('Researched tabs', researchedCount)}
          ${renderMetric('Empty tabs', Math.max(tabs.length - researchedCount, 0))}
        </div>
      </section>
      ${reports.map((report) => report.html).join('')}
    `,
    charts: reports.flatMap((report) => report.charts),
  });
}

export function safeFilename(value, fallback = 'market-research') {
  const cleaned = String(value || fallback)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return cleaned || fallback;
}

function renderDocument({ title, headerHtml, tocHtml, bodyHtml, charts }) {
  const chartPayload = safeJson(charts);
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${escapeHtml(title)}</title>
  <script src="https://cdn.jsdelivr.net/npm/chart.js"></script>
  <style>${REPORT_CSS}</style>
</head>
<body>
  <div class="container">
    ${headerHtml}
    ${tocHtml}
    ${bodyHtml}
  </div>
  <script>
    const exportCharts = ${chartPayload};
    const chartColors = ${safeJson(CHART_COLORS)};
    const moneyLabel = (value) => {
      if (typeof value !== 'number' || Number.isNaN(value)) return 'N/A';
      return '$' + value.toFixed(2);
    };
    const pctLabel = (value) => {
      if (typeof value !== 'number' || Number.isNaN(value)) return 'N/A';
      return value.toFixed(1) + '%';
    };
    exportCharts.forEach((chart) => {
      const node = document.getElementById(chart.id);
      if (!node || typeof Chart === 'undefined') return;
      const ctx = node.getContext('2d');
      if (chart.kind === 'price') {
        new Chart(ctx, {
          type: 'line',
          data: {
            labels: chart.labels,
            datasets: chart.datasets.map((dataset, index) => ({
              label: dataset.label,
              data: dataset.data,
              borderColor: chartColors[index % chartColors.length],
              backgroundColor: chartColors[index % chartColors.length] + '22',
              tension: 0.3,
              spanGaps: true
            }))
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
              legend: { position: 'bottom' },
              tooltip: { callbacks: { label: (item) => item.dataset.label + ': ' + moneyLabel(item.raw) } }
            },
            scales: { y: { beginAtZero: true, ticks: { callback: moneyLabel } } }
          }
        });
      }
      if (chart.kind === 'sentiment') {
        new Chart(ctx, {
          type: 'bar',
          data: {
            labels: chart.labels,
            datasets: [{
              label: 'Sentiment Score',
              data: chart.data,
              backgroundColor: chart.data.map((score) => score >= 85 ? '#10b981' : score >= 50 ? '#f59e0b' : '#ef4444'),
              borderRadius: 4
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: 'y',
            plugins: {
              legend: { display: false },
              tooltip: { callbacks: { label: (item) => 'Sentiment: ' + item.raw.toFixed(0) + '%' } }
            },
            scales: { x: { min: 0, max: 100, ticks: { callback: (value) => value + '%' } } }
          }
        });
      }
      if (chart.kind === 'market-share-bar') {
        new Chart(ctx, {
          type: 'bar',
          data: {
            labels: chart.labels,
            datasets: chart.datasets.map((dataset, index) => ({
              label: dataset.label,
              data: dataset.data,
              backgroundColor: chartColors[index % chartColors.length]
            }))
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { position: 'bottom' },
              tooltip: { callbacks: { label: (item) => item.dataset.label + ': ' + pctLabel(item.raw) } }
            },
            scales: {
              x: { stacked: true },
              y: { stacked: true, min: 0, max: 100, ticks: { callback: (value) => value + '%' } }
            }
          }
        });
      }
      if (chart.kind === 'market-share-pie') {
        new Chart(ctx, {
          type: 'pie',
          data: {
            labels: chart.labels,
            datasets: [{
              data: chart.data,
              backgroundColor: chart.labels.map((_, index) => chartColors[index % chartColors.length])
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { position: 'bottom' },
              tooltip: { callbacks: { label: (item) => item.label + ': ' + pctLabel(item.raw) } }
            }
          }
        });
      }
    });
  </script>
</body>
</html>`;
}

function renderTabReport(tab, options) {
  const data = tab?.data;
  const prefix = options.prefix;
  const sectionId = options.sectionId;
  const title = safeText(tab?.title || tab?.product || `Tab ${(options.tabIndex ?? 0) + 1}`);

  if (!data) {
    return {
      charts: [],
      tocItems: [],
      html: `
        <section class="card tab-section empty-tab" id="${escapeAttribute(sectionId)}">
          <div class="tab-heading">
            <div>
              <p class="eyebrow">Tab ${(options.tabIndex ?? 0) + 1}${options.isActive ? ' - Active' : ''}</p>
              <h2>${escapeHtml(title)}</h2>
            </div>
          </div>
          <p class="muted">No research results saved for this tab.</p>
        </section>
      `,
    };
  }

  const charts = [];
  const tocItems = [];
  const addSection = (key, html) => {
    if (!html) return '';
    const id = `${sectionId}-${key}`;
    tocItems.push({ id, label: SECTION_LABELS[key] || key });
    return html.replace('__SECTION_ID__', escapeAttribute(id));
  };

  const competitors = Array.isArray(data.competitors) ? data.competitors : [];
  const selectedNames = getSelectedNames(tab);
  const selectedCompetitors = competitors.filter((competitor) => selectedNames.includes(competitor?.name));
  const priceChart = buildPriceChart(`${prefix}-price-chart`, competitors);
  const sentimentChart = buildSentimentChart(`${prefix}-sentiment-chart`, competitors);
  if (priceChart) charts.push(priceChart);
  if (sentimentChart) charts.push(sentimentChart);

  const marketShareCharts = buildMarketShareCharts(prefix, tab.marketShare);
  charts.push(...marketShareCharts);

  const intro = options.includeTitle
    ? `
      <section class="tab-section" id="${escapeAttribute(sectionId)}">
        <div class="tab-heading">
          <div>
            <p class="eyebrow">Tab ${(options.tabIndex ?? 0) + 1}${options.isActive ? ' - Active' : ''}</p>
            <h2>${escapeHtml(title)}</h2>
          </div>
          <span class="pill">${escapeHtml(safeText(tab?.product || 'Research'))}</span>
        </div>
      </section>
    `
    : '';

  const sections = [
    addSection('overview', renderOverviewSection({
      sectionId: '__SECTION_ID__',
      tab,
      data,
      priceChartId: priceChart?.id,
      sentimentChartId: sentimentChart?.id,
    })),
    addSection('competitors', renderCompetitorsSection('__SECTION_ID__', competitors)),
    addSection('trends', renderMarketTrendsSection('__SECTION_ID__', data.market_trends)),
    addSection('trend-analysis', renderTrendAnalysesSection('__SECTION_ID__', tab.trendAnalyses)),
    addSection('market-share', renderMarketShareSection('__SECTION_ID__', tab.marketShare, tab.marketShareWarning, marketShareCharts)),
    addSection('seo', renderSeoSection('__SECTION_ID__', tab.seoAnalysis, tab.seoWarning)),
    addSection('events', renderEventsSection('__SECTION_ID__', tab.events)),
    addSection('deep-dives', renderDeepDivesSection('__SECTION_ID__', tab.deepDives)),
    addSection('sources', renderFurtherReadingSection('__SECTION_ID__', tab, data)),
    addSection('selected-comparison', renderSelectedComparisonSection('__SECTION_ID__', selectedCompetitors)),
  ].join('');

  return {
    charts,
    tocItems,
    html: `${intro}${sections}`,
  };
}

function renderReportHeader({ title, subtitle }) {
  return `
    <header class="report-header">
      <div>
        <p class="eyebrow">Market Scan Export</p>
        <h1>${escapeHtml(title)}</h1>
        <p class="meta">${escapeHtml(subtitle)}</p>
      </div>
    </header>
  `;
}

function renderToc(items) {
  if (!items.length) return '';
  return `
    <nav class="card toc" aria-label="Report sections">
      <h2>Contents</h2>
      <div class="toc-grid">
        ${items.map((item) => `<a href="#${escapeAttribute(item.id)}">${escapeHtml(item.label)}</a>`).join('')}
      </div>
    </nav>
  `;
}

function renderOverviewSection({ sectionId, tab, data, priceChartId, sentimentChartId }) {
  return `
    <section class="card" id="${sectionId}">
      <h2>Overview</h2>
      <div class="summary-box">
        <strong>Market Summary:</strong> ${escapeHtml(safeText(data.market_summary || 'No summary available.'))}
      </div>
      <div class="metric-grid">
        ${renderMetric('Product', safeText(tab?.product || 'Not specified'))}
        ${renderMetric('Location', safeText(tab?.location || 'Not specified'))}
        ${renderMetric('Research window', tab?.yearsBack ? `${safeText(tab.yearsBack)} years` : 'Not specified')}
        ${renderMetric('Competitors', Array.isArray(data.competitors) ? data.competitors.length : 0)}
      </div>
      ${(priceChartId || sentimentChartId) ? `
        <div class="chart-grid">
          ${priceChartId ? renderChartCard('Price History', priceChartId) : ''}
          ${sentimentChartId ? renderChartCard('Sentiment Comparison', sentimentChartId) : ''}
        </div>
      ` : ''}
    </section>
  `;
}

function renderCompetitorsSection(sectionId, competitors) {
  if (!competitors.length) return '';
  return `
    <section class="card" id="${sectionId}">
      <h2>Competitors (${competitors.length})</h2>
      <div class="competitor-list">
        ${competitors.map((competitor) => renderCompetitor(competitor)).join('')}
      </div>
    </section>
  `;
}

function renderCompetitor(competitor) {
  const priceRange = competitor?.price_range;
  return `
    <article class="competitor">
      <div class="competitor-header">
        <div>
          <h3>${escapeHtml(safeText(competitor?.name || 'Unknown competitor'))}</h3>
          ${renderSafeLink(competitor?.website, 'Visit website')}
        </div>
        <div class="price">${escapeHtml(formatMoney(competitor?.current_price, competitor?.currency))}</div>
      </div>
      ${renderSentiment(competitor?.sentiment_score)}
      ${competitor?.review_summary ? `<p class="body-copy">${escapeHtml(competitor.review_summary)}</p>` : ''}
      ${renderTags(competitor?.key_features)}
      ${(priceRange || hasList(competitor?.pros) || hasList(competitor?.cons)) ? `
        <div class="detail-grid">
          ${priceRange ? renderMiniPanel('Historical Price Range', escapeHtml(`${formatMoney(priceRange.min, competitor?.currency)} - ${formatMoney(priceRange.max, competitor?.currency)}`)) : ''}
          ${hasList(competitor?.pros) ? renderMiniPanel('Pros', renderList(competitor.pros)) : ''}
          ${hasList(competitor?.cons) ? renderMiniPanel('Cons', renderList(competitor.cons)) : ''}
        </div>
      ` : ''}
    </article>
  `;
}

function renderMarketTrendsSection(sectionId, trends) {
  if (!hasList(trends)) return '';
  return `
    <section class="card" id="${sectionId}">
      <h2>Market Trends</h2>
      <div class="trend-grid">
        ${trends.map((trend) => `
          <div class="trend trend-${escapeAttribute(String(trend?.impact || 'neutral').toLowerCase())}">
            <span>${escapeHtml(safeText(trend?.impact || 'neutral'))}</span>
            <p>${escapeHtml(safeText(trend?.trend || trend))}</p>
          </div>
        `).join('')}
      </div>
    </section>
  `;
}

function renderTrendAnalysesSection(sectionId, trendAnalyses) {
  const entries = completedEntries(trendAnalyses);
  if (!entries.length) return '';
  return `
    <section class="card" id="${sectionId}">
      <h2>Trend Analyses</h2>
      ${entries.map(([name, entry]) => {
        const data = entry.data || {};
        return `
          <article class="analysis-block">
            <div class="block-header">
              <h3>${escapeHtml(name)}</h3>
              ${data.confidence_level ? `<span class="pill">${escapeHtml(data.confidence_level)} confidence</span>` : ''}
            </div>
            ${renderParagraph('Executive Summary', data.executive_summary)}
            ${renderSimpleSubsection('Key Drivers', data.drivers)}
            ${renderParagraph('Market Implications', data.market_implications)}
            ${renderImpactBySegment(data.impact_by_segment)}
            <div class="two-col">
              ${renderSimpleSubsection('Opportunities', data.opportunities)}
              ${renderSimpleSubsection('Risks', data.risks)}
            </div>
            ${renderParagraph('Timeline', data.timeline)}
            ${renderParagraph('Competitive Positioning', data.competitive_positioning)}
            ${renderTagsBlock('Key Players Affected', data.key_players_affected)}
            ${renderOrderedSubsection('Strategic Recommendations', data.strategic_recommendations)}
            ${renderSources(data.grounding_sources)}
          </article>
        `;
      }).join('')}
    </section>
  `;
}

function renderMarketShareSection(sectionId, marketShare, warning, charts) {
  if (!marketShare) return '';
  const competitors = Array.isArray(marketShare.competitors) ? marketShare.competitors : [];
  const barChart = charts.find((chart) => chart.kind === 'market-share-bar');
  const pieChart = charts.find((chart) => chart.kind === 'market-share-pie');
  return `
    <section class="card" id="${sectionId}">
      <h2>Market Share Breakdown</h2>
      ${warning ? renderWarning(warning) : ''}
      ${marketShare.summary ? `<p class="body-copy">${escapeHtml(marketShare.summary)}</p>` : ''}
      ${(barChart || pieChart) ? `
        <div class="chart-grid">
          ${barChart ? renderChartCard('Share Over Time', barChart.id) : ''}
          ${pieChart ? renderChartCard(`Latest Year Distribution${pieChart.year ? ` (${pieChart.year})` : ''}`, pieChart.id) : ''}
        </div>
      ` : ''}
      ${competitors.length ? `
        <div class="table-wrap">
          <table>
            <thead><tr><th>Competitor</th><th>Trend</th><th>Positioning</th></tr></thead>
            <tbody>
              ${competitors.map((competitor) => `
                <tr>
                  <td>${escapeHtml(safeText(competitor?.name || 'Unknown'))}</td>
                  <td>${escapeHtml(safeText(competitor?.trend || 'Not specified'))}</td>
                  <td>${escapeHtml(safeText(competitor?.positioning || 'Not specified'))}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      ` : '<p class="muted">No market share rows were returned.</p>'}
      ${renderParagraph('Market Dynamics', marketShare.market_dynamics)}
      <div class="meta-row">
        ${marketShare.data_confidence ? `<span>Confidence: ${escapeHtml(marketShare.data_confidence)}</span>` : ''}
        ${marketShare.methodology ? `<span>${escapeHtml(marketShare.methodology)}</span>` : ''}
      </div>
    </section>
  `;
}

function renderSeoSection(sectionId, seo, warning) {
  if (!seo) return '';
  return `
    <section class="card" id="${sectionId}">
      <h2>SEO Landscape</h2>
      ${warning ? renderWarning(warning) : ''}
      ${renderParagraph('Executive Summary', seo.competitive_summary)}
      <div class="detail-grid">
        ${seo.market_keywords ? renderMiniPanel('Target Keywords', `
          ${renderTags(seo.market_keywords.primary_keywords)}
          ${renderTags(seo.market_keywords.long_tail_keywords)}
          ${seo.market_keywords.keyword_difficulty ? `<p class="muted">Difficulty: ${escapeHtml(seo.market_keywords.keyword_difficulty)}</p>` : ''}
        `) : ''}
        ${seo.domain_authority_landscape ? renderMiniPanel('Domain Authority', renderKeyValueList(seo.domain_authority_landscape)) : ''}
        ${renderObjectCards('Content Themes', seo.content_themes, ['theme', 'frequency', 'description'])}
        ${renderObjectCards('Backlink Strategies', seo.backlink_strategies, ['strategy', 'effectiveness', 'description'])}
        ${renderObjectCards('Content Gaps', seo.content_gaps, ['opportunity', 'potential_impact', 'difficulty'])}
        ${renderObjectCards('Technical SEO Trends', seo.technical_seo_trends, ['aspect', 'importance', 'market_adoption'])}
        ${seo.local_seo_factors?.relevant ? renderMiniPanel('Local SEO Factors', `
          ${renderSimpleSubsection('Factors', seo.local_seo_factors.factors)}
          ${renderSimpleSubsection('Optimization Tips', seo.local_seo_factors.optimization_tips)}
        `) : ''}
      </div>
    </section>
  `;
}

function renderEventsSection(sectionId, events) {
  const entries = completedEntries(events);
  if (!entries.length) return '';
  return `
    <section class="card" id="${sectionId}">
      <h2>Events and Headlines</h2>
      ${entries.map(([name, entry]) => {
        const data = entry.data || {};
        return `
          <article class="analysis-block">
            <div class="block-header">
              <h3>${escapeHtml(name)}</h3>
              ${entry.warning ? '<span class="pill warning-pill">Warning</span>' : ''}
            </div>
            ${entry.warning ? renderWarning(entry.warning) : ''}
            ${renderParagraph('Market Narrative', data.market_narrative)}
            ${hasList(data.headlines) ? `
              <h4>Recent Headlines (${data.headlines.length})</h4>
              <div class="headline-list">
                ${data.headlines.map((headline) => `
                  <div class="headline">
                    <div class="headline-meta">
                      <span>${escapeHtml(safeText(headline?.date || 'Date unavailable'))}</span>
                      <span>${escapeHtml(safeText(headline?.category || 'other'))}</span>
                      <span>${escapeHtml(safeText(headline?.significance || 'unknown'))} impact</span>
                      <span>${escapeHtml(safeText(headline?.sentiment || 'neutral'))}</span>
                    </div>
                    <h5>${escapeHtml(safeText(headline?.title || 'Untitled headline'))}</h5>
                    ${headline?.summary ? `<p>${escapeHtml(headline.summary)}</p>` : ''}
                    ${headline?.sources_hint ? `<p class="muted">Sources: ${escapeHtml(headline.sources_hint)}</p>` : ''}
                  </div>
                `).join('')}
              </div>
            ` : '<p class="muted">No recent headlines found for this competitor.</p>'}
            ${renderObjectCards('Trending Themes', data.trending_themes, ['theme', 'frequency', 'description'])}
            ${renderParagraph('Competitive Implications', data.competitive_implications)}
            ${renderSources(data.grounding_sources)}
          </article>
        `;
      }).join('')}
    </section>
  `;
}

function renderDeepDivesSection(sectionId, deepDives) {
  const entries = completedEntries(deepDives);
  if (!entries.length) return '';
  return `
    <section class="card" id="${sectionId}">
      <h2>Deep Dives</h2>
      ${entries.map(([name, entry]) => {
        const data = entry.data || {};
        return `
          <article class="analysis-block">
            <h3>${escapeHtml(name)}</h3>
            ${renderParagraph('Executive Summary', data.detailed_business_summary)}
            ${data.key_metrics ? renderMiniPanel('Key Metrics', renderKeyValueList(data.key_metrics)) : ''}
            ${data.target_market ? renderMiniPanel('Target Market', renderKeyValueList(data.target_market)) : ''}
            ${renderParagraph('Value Proposition', data.value_proposition)}
            <div class="two-col">
              ${renderSimpleSubsection('Strengths', data.strengths)}
              ${renderSimpleSubsection('Weaknesses', data.weaknesses)}
              ${renderSimpleSubsection('Opportunities', data.opportunities)}
              ${renderSimpleSubsection('Risks', data.risks)}
            </div>
            ${renderSimpleSubsection('Competitive Differentiators', data.competitive_differentiators)}
            ${renderSimpleSubsection('Key Partnerships and Channels', data.key_partnerships_or_channels)}
            ${renderSimpleSubsection('Recent Notable Developments', data.recent_notable_developments)}
            ${renderParagraph('Competitive Intensity', data.competitive_intensity || data.competitive_threat_level)}
            ${renderParagraph('Strategic Verdict', data.summary_verdict)}
            ${renderSources(data.grounding_sources)}
          </article>
        `;
      }).join('')}
    </section>
  `;
}

function renderFurtherReadingSection(sectionId, tab, data) {
  const sources = tab?.furtherReadingSources;
  const isOldFormat = sources && (sources.search_links || sources.industry_resources);
  const normalized = isOldFormat ? {} : (sources || {});
  const initialSources = Array.isArray(data?.grounding_sources) ? data.grounding_sources : [];
  const competitorSites = (Array.isArray(data?.competitors) ? data.competitors : []).filter((competitor) => competitor?.website);
  const hasContent = hasList(normalized.citations) ||
    hasList(initialSources) ||
    competitorSites.length > 0 ||
    ['reports', 'news', 'reviews', 'data'].some((key) => hasList(normalized[key]));

  if (!hasContent) return '';

  return `
    <section class="card" id="${sectionId}">
      <h2>Sources and Further Reading</h2>
      ${hasList(normalized.citations) ? renderSourceLinkGroup('Citations', normalized.citations, true) : ''}
      ${hasList(initialSources) ? renderSourceLinkGroup('Sources Used In Initial Research', initialSources.map((source) => ({
        title: source?.title || source?.uri || source?.url,
        url: source?.uri || source?.url,
        domain: domainFromUrl(source?.uri || source?.url),
      }))) : ''}
      ${competitorSites.length ? `
        <h3>Competitor Sites</h3>
        <div class="tag-row">
          ${competitorSites.map((site) => renderSafeLink(site.website, site.name, 'tag-link')).join('')}
        </div>
      ` : ''}
      <div class="detail-grid">
        ${['reports', 'news', 'reviews', 'data'].map((key) =>
          hasList(normalized[key]) ? renderSourceLinkGroup(titleCase(key), normalized[key]) : ''
        ).join('')}
      </div>
      <p class="fine-print">Resources found through live web search. Verify data independently.</p>
    </section>
  `;
}

function renderSelectedComparisonSection(sectionId, competitors) {
  if (!competitors.length) return '';
  const prices = competitors.map((competitor) => numberOrNull(competitor?.current_price)).filter((value) => value !== undefined);
  const avgSentiment = competitors.reduce((sum, competitor) => sum + (numberOrNull(competitor?.sentiment_score) || 0), 0) / competitors.length;
  return `
    <section class="card" id="${sectionId}">
      <h2>Selected Competitor Comparison</h2>
      <div class="metric-grid">
        ${prices.length ? renderMetric('Price range', `${formatMoney(Math.min(...prices))} - ${formatMoney(Math.max(...prices))}`) : ''}
        ${renderMetric('Average sentiment', `${Math.round(avgSentiment * 100)}%`)}
        ${renderMetric('Selected competitors', competitors.length)}
      </div>
      <div class="table-wrap">
        <table>
          <thead><tr><th>Competitor</th><th>Price</th><th>Sentiment</th><th>Features</th></tr></thead>
          <tbody>
            ${competitors.map((competitor) => `
              <tr>
                <td>${escapeHtml(safeText(competitor?.name || 'Unknown'))}</td>
                <td>${escapeHtml(formatMoney(competitor?.current_price, competitor?.currency))}</td>
                <td>${escapeHtml(formatSentiment(competitor?.sentiment_score))}</td>
                <td>${escapeHtml((competitor?.key_features || []).join(', ') || 'Not specified')}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    </section>
  `;
}

function buildPriceChart(id, competitors) {
  const series = competitors
    .filter((competitor) => hasList(competitor?.price_history))
    .map((competitor) => ({
      label: safeText(competitor.name || 'Unknown'),
      history: competitor.price_history
        .map((point) => ({ date: point?.date, price: numberOrNull(point?.price) }))
        .filter((point) => point.date && point.price !== undefined),
    }))
    .filter((competitor) => competitor.history.length);

  if (!series.length) return null;
  const labels = Array.from(new Set(series.flatMap((competitor) => competitor.history.map((point) => point.date)))).sort();
  if (!labels.length) return null;

  return {
    id,
    kind: 'price',
    labels: labels.map((date) => formatChartDate(date)),
    datasets: series.map((competitor) => ({
      label: competitor.label,
      data: labels.map((date) => {
        const point = competitor.history.find((item) => item.date === date);
        return point ? point.price : undefined;
      }),
    })),
  };
}

function buildSentimentChart(id, competitors) {
  const rows = competitors
    .map((competitor) => ({
      label: safeText(competitor?.name || 'Unknown'),
      value: numberOrNull(competitor?.sentiment_score),
    }))
    .filter((row) => row.value !== undefined);
  if (!rows.length) return null;
  return {
    id,
    kind: 'sentiment',
    labels: rows.map((row) => truncate(row.label, 28)),
    data: rows.map((row) => Math.round(row.value * 100)),
  };
}

function buildMarketShareCharts(prefix, marketShare) {
  if (!marketShare || !hasList(marketShare.competitors)) return [];
  const competitors = marketShare.competitors;
  const yearSet = new Set();
  competitors.forEach((competitor) => {
    Object.keys(competitor?.shares_by_year || {}).forEach((year) => yearSet.add(year));
  });
  Object.keys(marketShare.other_share_by_year || {}).forEach((year) => yearSet.add(year));
  const years = Array.from(yearSet).sort();
  if (!years.length) return [];

  const datasets = competitors.map((competitor) => ({
    label: safeText(competitor?.name || 'Unknown'),
    data: years.map((year) => numberOrNull(competitor?.shares_by_year?.[year]) || 0),
  }));

  if (marketShare.other_share_by_year) {
    datasets.push({
      label: 'Other / Rest of Market',
      data: years.map((year) => numberOrNull(marketShare.other_share_by_year?.[year]) || 0),
    });
  }

  const latestYear = years[years.length - 1];
  const latestRows = datasets
    .map((dataset) => ({ label: dataset.label, value: dataset.data[years.indexOf(latestYear)] || 0 }))
    .filter((row) => row.value > 0);

  const charts = [{
    id: `${prefix}-market-share-bar-chart`,
    kind: 'market-share-bar',
    labels: years,
    datasets,
  }];

  if (latestRows.length) {
    charts.push({
      id: `${prefix}-market-share-pie-chart`,
      kind: 'market-share-pie',
      labels: latestRows.map((row) => row.label),
      data: latestRows.map((row) => row.value),
      year: latestYear,
    });
  }

  return charts;
}

function renderChartCard(title, id) {
  return `
    <div class="chart-card">
      <h3>${escapeHtml(title)}</h3>
      <div class="chart-container">
        <canvas id="${escapeAttribute(id)}"></canvas>
      </div>
    </div>
  `;
}

function renderMetric(label, value) {
  return `
    <div class="metric">
      <span>${escapeHtml(label)}</span>
      <strong>${escapeHtml(safeText(value))}</strong>
    </div>
  `;
}

function renderSentiment(value) {
  const score = numberOrNull(value);
  if (score === undefined) return '<span class="sentiment neutral">Sentiment: N/A</span>';
  const cls = score >= 0.85 ? 'positive' : score >= 0.5 ? 'neutral' : 'negative';
  return `<span class="sentiment ${cls}">Sentiment: ${Math.round(score * 100)}%</span>`;
}

function renderTags(items) {
  if (!hasList(items)) return '';
  return `<div class="tag-row">${items.map((item) => `<span class="tag">${escapeHtml(item)}</span>`).join('')}</div>`;
}

function renderTagsBlock(title, items) {
  if (!hasList(items)) return '';
  return `
    <div>
      <h4>${escapeHtml(title)}</h4>
      ${renderTags(items)}
    </div>
  `;
}

function renderList(items) {
  if (!hasList(items)) return '';
  return `<ul>${items.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>`;
}

function renderSimpleSubsection(title, items) {
  if (!hasList(items)) return '';
  return `
    <div>
      <h4>${escapeHtml(title)}</h4>
      ${renderList(items)}
    </div>
  `;
}

function renderOrderedSubsection(title, items) {
  if (!hasList(items)) return '';
  return `
    <div>
      <h4>${escapeHtml(title)}</h4>
      <ol>${items.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ol>
    </div>
  `;
}

function renderParagraph(title, value) {
  if (value === undefined || value === null || String(value).trim() === '') return '';
  return `
    <div>
      <h4>${escapeHtml(title)}</h4>
      <p class="body-copy">${escapeHtml(value)}</p>
    </div>
  `;
}

function renderMiniPanel(title, content) {
  if (!content) return '';
  return `
    <div class="mini-panel">
      <h4>${escapeHtml(title)}</h4>
      ${content}
    </div>
  `;
}

function renderImpactBySegment(items) {
  if (!hasList(items)) return '';
  return `
    <div>
      <h4>Impact by Segment</h4>
      <div class="detail-grid">
        ${items.map((item) => `
          <div class="mini-panel">
            <h4>${escapeHtml(safeText(item?.segment || 'Segment'))}</h4>
            <p class="body-copy">${escapeHtml(safeText(item?.impact || 'No impact summary provided.'))}</p>
            ${item?.severity ? `<span class="pill">${escapeHtml(item.severity)} severity</span>` : ''}
          </div>
        `).join('')}
      </div>
    </div>
  `;
}

function renderObjectCards(title, items, fields) {
  if (!hasList(items)) return '';
  return renderMiniPanel(title, `
    <div class="object-list">
      ${items.map((item) => `
        <div class="object-card">
          ${fields.map((field, index) => {
            const value = item?.[field];
            if (value === undefined || value === null || String(value).trim() === '') return '';
            const cls = index === 0 ? 'object-title' : 'muted';
            return `<p class="${cls}">${escapeHtml(value)}</p>`;
          }).join('')}
        </div>
      `).join('')}
    </div>
  `);
}

function renderKeyValueList(obj) {
  if (!obj || typeof obj !== 'object') return '';
  return `
    <dl class="key-value">
      ${Object.entries(obj)
        .filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== '')
        .map(([key, value]) => `
          <div>
            <dt>${escapeHtml(titleCase(key.replace(/_/g, ' ')))}</dt>
            <dd>${Array.isArray(value) ? escapeHtml(value.join(', ')) : escapeHtml(value)}</dd>
          </div>
        `).join('')}
    </dl>
  `;
}

function renderSourceLinkGroup(title, items, includeReason = false) {
  if (!hasList(items)) return '';
  return `
    <div class="source-group">
      <h3>${escapeHtml(title)}</h3>
      <div class="source-list">
        ${items.map((item) => {
          const url = item?.url || item?.uri;
          const text = item?.title || url || 'Source';
          const reason = includeReason && item?.reason ? `<p>${escapeHtml(item.reason)}</p>` : '';
          const domain = item?.domain || domainFromUrl(url);
          return `
            <div class="source-item">
              ${renderSafeLink(url, text)}
              ${domain ? `<span>${escapeHtml(domain)}</span>` : ''}
              ${item?.category ? `<span>${escapeHtml(item.category)}</span>` : ''}
              ${reason}
            </div>
          `;
        }).join('')}
      </div>
    </div>
  `;
}

function renderSources(sources) {
  return renderSourceLinkGroup('Sources', sources);
}

function renderSafeLink(url, text, className = '') {
  const href = safeUrl(url);
  if (!href) return text ? `<span class="${escapeAttribute(className)}">${escapeHtml(text)}</span>` : '';
  return `<a class="${escapeAttribute(className)}" href="${escapeAttribute(href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(text || href)}</a>`;
}

function renderWarning(message) {
  return `<div class="warning">${escapeHtml(message)}</div>`;
}

function metaLine(parts) {
  return parts.filter(Boolean).join(' | ');
}

function completedEntries(obj) {
  return Object.entries(obj || {}).filter(([, entry]) => entry?.data);
}

function getSelectedNames(tab) {
  if (tab?.selectedCompetitors instanceof Set) return Array.from(tab.selectedCompetitors);
  if (Array.isArray(tab?.selectedCompetitors)) return tab.selectedCompetitors;
  return [];
}

function hasList(value) {
  return Array.isArray(value) && value.length > 0;
}

function numberOrNull(value) {
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : undefined;
}

function formatMoney(value, currency = 'USD') {
  const num = numberOrNull(value);
  if (num === undefined) return 'N/A';
  const currencyText = currency ? `${safeText(currency)} ` : '';
  return `${currencyText}${num.toFixed(2)}`;
}

function formatSentiment(value) {
  const score = numberOrNull(value);
  return score === undefined ? 'N/A' : `${Math.round(score * 100)}%`;
}

function formatDateTime(date) {
  try {
    return date.toLocaleString();
  } catch {
    return String(date);
  }
}

function formatChartDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return safeText(value);
  return date.toLocaleString('default', { month: 'short', year: 'numeric' });
}

function domainFromUrl(url) {
  const safe = safeUrl(url);
  if (!safe) return '';
  try {
    return new URL(safe).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

function safeUrl(value) {
  if (!value) return '';
  try {
    const url = new URL(String(value));
    if (!['http:', 'https:'].includes(url.protocol)) return '';
    return url.href;
  } catch {
    return '';
  }
}

function safeText(value) {
  if (value === undefined || value === null) return '';
  return String(value);
}

function titleCase(value) {
  return safeText(value).replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
}

function truncate(value, max) {
  const text = safeText(value);
  return text.length > max ? `${text.slice(0, max - 3)}...` : text;
}

function escapeHtml(value) {
  return safeText(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttribute(value) {
  return escapeHtml(value).replace(/`/g, '&#96;');
}

function safeJson(value) {
  return JSON.stringify(value)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}

const REPORT_CSS = `
  * { box-sizing: border-box; }
  html { scroll-behavior: smooth; }
  body {
    margin: 0;
    background: #f4f6f8;
    color: #111827;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
    line-height: 1.5;
  }
  a { color: #2563eb; text-decoration: none; }
  a:hover { text-decoration: underline; }
  .container { max-width: 1180px; margin: 0 auto; padding: 24px; }
  .report-header {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 28px;
    margin-bottom: 18px;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
  }
  .report-header h1 { margin: 0 0 8px 0; font-size: 30px; line-height: 1.15; }
  .eyebrow {
    margin: 0 0 6px 0;
    color: #6b7280;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .meta, .muted, .fine-print { color: #6b7280; }
  .meta { margin: 0; font-size: 13px; }
  .fine-print { font-size: 11px; font-style: italic; margin-top: 16px; }
  .card {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 22px;
    margin-bottom: 18px;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
  }
  .card h2 { margin: 0 0 16px 0; font-size: 18px; }
  h3 { margin: 0 0 10px 0; font-size: 15px; }
  h4 { margin: 14px 0 8px 0; font-size: 12px; color: #4b5563; text-transform: uppercase; letter-spacing: 0.04em; }
  h5 { margin: 0 0 6px 0; font-size: 14px; }
  .toc h2 { font-size: 15px; margin-bottom: 12px; }
  .toc-grid { display: flex; flex-wrap: wrap; gap: 8px; }
  .toc-grid a {
    display: inline-flex;
    padding: 6px 10px;
    border: 1px solid #dbeafe;
    border-radius: 999px;
    background: #eff6ff;
    color: #1d4ed8;
    font-size: 12px;
    font-weight: 600;
  }
  .tab-section { margin-top: 28px; scroll-margin-top: 16px; }
  .tab-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
  .tab-heading h2 { margin-bottom: 0; }
  .summary-box {
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    padding: 14px 16px;
    margin-bottom: 16px;
    color: #1e3a8a;
  }
  .metric-grid, .detail-grid, .chart-grid, .trend-grid, .two-col {
    display: grid;
    gap: 12px;
  }
  .metric-grid { grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); margin-bottom: 16px; }
  .detail-grid { grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); }
  .chart-grid { grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); margin-top: 16px; }
  .trend-grid { grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); }
  .two-col { grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); }
  .metric, .mini-panel, .chart-card, .object-card {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #f9fafb;
    padding: 12px;
  }
  .metric span { display: block; color: #6b7280; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; }
  .metric strong { display: block; margin-top: 4px; font-size: 15px; }
  .chart-container { height: 310px; }
  .competitor-list, .headline-list, .source-list, .object-list { display: flex; flex-direction: column; gap: 12px; }
  .competitor {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 16px;
    background: #ffffff;
  }
  .competitor-header { display: flex; justify-content: space-between; gap: 14px; align-items: flex-start; }
  .competitor h3 { margin: 0 0 4px 0; }
  .price { font-size: 19px; font-weight: 800; white-space: nowrap; }
  .sentiment, .pill {
    display: inline-flex;
    align-items: center;
    width: fit-content;
    padding: 4px 9px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    margin: 8px 0;
  }
  .positive { background: #dcfce7; color: #166534; }
  .neutral { background: #fef3c7; color: #92400e; }
  .negative { background: #fee2e2; color: #991b1b; }
  .pill { background: #eef2ff; color: #3730a3; margin: 0; }
  .warning-pill { background: #fef3c7; color: #92400e; }
  .tag-row { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
  .tag, .tag-link {
    display: inline-flex;
    align-items: center;
    padding: 4px 9px;
    border-radius: 999px;
    background: #f3f4f6;
    color: #374151;
    font-size: 12px;
  }
  .body-copy { margin: 8px 0 0 0; color: #374151; font-size: 13px; }
  .analysis-block {
    border-top: 1px solid #e5e7eb;
    padding-top: 16px;
    margin-top: 16px;
  }
  .analysis-block:first-of-type { border-top: 0; padding-top: 0; }
  .block-header { display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; }
  .trend {
    border-left: 4px solid #6b7280;
    border-radius: 8px;
    padding: 12px;
    background: #f9fafb;
  }
  .trend span { display: block; color: #6b7280; font-size: 11px; font-weight: 800; text-transform: uppercase; }
  .trend p { margin: 4px 0 0 0; font-size: 13px; }
  .trend-positive { background: #f0fdf4; border-left-color: #10b981; }
  .trend-negative { background: #fef2f2; border-left-color: #ef4444; }
  .warning {
    margin-bottom: 12px;
    padding: 10px 12px;
    border: 1px solid #f59e0b66;
    border-radius: 8px;
    background: #fffbeb;
    color: #92400e;
    font-size: 13px;
  }
  .headline {
    border-left: 4px solid #9ca3af;
    border-radius: 8px;
    background: #f9fafb;
    padding: 12px;
  }
  .headline-meta, .meta-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    color: #6b7280;
    font-size: 11px;
  }
  .headline-meta span, .meta-row span {
    border: 1px solid #e5e7eb;
    border-radius: 999px;
    padding: 2px 7px;
    background: #ffffff;
  }
  .headline p { margin: 4px 0 0 0; font-size: 13px; color: #4b5563; }
  .source-item {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 10px 12px;
    background: #f9fafb;
    font-size: 13px;
  }
  .source-item span { color: #6b7280; margin-left: 6px; font-size: 11px; }
  .source-item p { margin: 4px 0 0 0; color: #6b7280; font-size: 12px; }
  .table-wrap { overflow-x: auto; margin-top: 12px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th, td { padding: 10px 12px; border-bottom: 1px solid #e5e7eb; text-align: left; vertical-align: top; }
  th { color: #4b5563; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; }
  ul, ol { margin: 6px 0 0 0; padding-left: 18px; }
  li { margin-bottom: 4px; font-size: 13px; color: #374151; }
  .key-value { margin: 0; }
  .key-value div { display: grid; grid-template-columns: minmax(110px, 0.8fr) 1.2fr; gap: 10px; padding: 6px 0; border-bottom: 1px dashed #e5e7eb; }
  .key-value div:last-child { border-bottom: 0; }
  dt { color: #6b7280; font-size: 12px; }
  dd { margin: 0; color: #111827; font-size: 13px; }
  .object-title { margin: 0 0 4px 0; font-weight: 700; color: #111827; }
  .empty-tab { opacity: 0.86; }
  @media (max-width: 720px) {
    .container { padding: 12px; }
    .report-header { padding: 20px; }
    .report-header h1 { font-size: 24px; }
    .chart-grid, .detail-grid, .two-col { grid-template-columns: 1fr; }
    .competitor-header, .block-header, .tab-heading { flex-direction: column; }
    .chart-container { height: 260px; }
  }
  @media print {
    body { background: #ffffff; }
    .container { max-width: none; padding: 0; }
    .card, .report-header { box-shadow: none; break-inside: avoid; }
    .toc { display: none; }
    a { color: #111827; text-decoration: none; }
  }
`;
